import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import productItemEanApi from "../../services/productItemEanApi";

const emptyRow = () => ({
  id: null,
  eanCode: "",
  isPrimary: 0,
  status: 1,
});

const ProductItemEanModal = ({ show, onHide, productItem }) => {
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && productItem?.id) {
      loadData();
    }
  }, [show, productItem]);

  const loadData = async () => {
    try {
      setLoading(true);
      const resp = await productItemEanApi.getByItemId(productItem.id);
      const result = resp?.data || [];
      setRows(result.length ? result.map((x) => ({
        id: x.id,
        eanCode: x.eanCode || x.ean_code || "",
        isPrimary: Number(x.isPrimary ?? x.is_primary ?? 0),
        status: Number(x.status ?? 1),
      })) : [emptyRow()]);
    } catch (err) {
      toast.error("Failed to load EAN codes");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index][field] = value;

      if (field === "isPrimary" && Number(value) === 1) {
        updated.forEach((row, idx) => {
          if (idx !== index) row.isPrimary = 0;
        });
      }

      return updated;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const filtered = rows
      .map((x) => ({
        ...x,
        eanCode: String(x.eanCode || "").trim(),
      }))
      .filter((x) => x.eanCode);

    if (!filtered.length) {
      toast.error("Add at least one EAN code");
      return;
    }

    try {
      setLoading(true);
      await productItemEanApi.saveBulk({
        productItemId: productItem.id,
        itemRefCode: productItem.refCode,
        eanCodes: filtered,
      });
      toast.success("EAN codes updated successfully");
      onHide(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save EAN codes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Manage EAN Codes - {productItem?.productName || ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          <strong>Reference Code:</strong> {productItem?.refCode || "-"}
        </div>

        <Table bordered>
          <thead>
            <tr>
              <th style={{ width: "60%" }}>EAN Code</th>
              <th style={{ width: "20%" }}>Primary</th>
              <th style={{ width: "20%" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    type="text"
                    value={row.eanCode}
                    onChange={(e) => handleChange(index, "eanCode", e.target.value)}
                    placeholder="Enter EAN code"
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="radio"
                    name="primaryEan"
                    checked={Number(row.isPrimary) === 1}
                    onChange={() => handleChange(index, "isPrimary", 1)}
                  />
                </td>
                <td className="text-center">
                  <Button variant="danger" size="sm" onClick={() => removeRow(index)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Button variant="secondary" onClick={addRow}>
          Add EAN
        </Button>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={() => onHide(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductItemEanModal;